import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[10px] font-semibold tracking-[.01em] transition-[filter,transform,box-shadow] duration-150 cursor-pointer disabled:pointer-events-none disabled:opacity-50 hover:brightness-110 active:translate-y-px outline-none focus-visible:ring-[3px] focus-visible:ring-ring [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[1.05em]",
  {
    variants: {
      variant: {
        // pop.in signature gradient — primary actions
        default:
          "bg-brand-gradient text-white shadow-[0_2px_14px_rgba(124,58,237,.35)]",
        // flat brand fill
        solid: "bg-brand text-white",
        ghost: "bg-transparent text-muted-foreground border border-border",
        subtle: "bg-panel-2 text-foreground border border-border",
        danger:
          "bg-transparent text-[#ff7a7a] border border-[rgba(255,120,120,.35)]",
      },
      size: {
        sm: "px-3 py-[7px] text-[13px] gap-1.5",
        md: "px-4 py-2.5 text-sm",
        lg: "px-[22px] py-[13px] text-[15px] gap-2.5",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
